import {
  createDisabledPilotTelemetry,
  createQueuedPublishingPilotTelemetry,
} from "../pilot";
import { LEGACY_ENGINE_PREVIEW_BLOCKLIST } from "./legacyEnginePreviewPolicy";

export { readSavedVersionPreference, createVersionPreferences } from "./versionPreferences";
const pageExitDeliveryByWindow = new WeakMap();

const readPageExitDelivery = (windowObject) => {
  if (
    !windowObject ||
    (typeof windowObject !== "object" && typeof windowObject !== "function")
  ) {
    return Object.freeze({ urgent: false });
  }
  const known = pageExitDeliveryByWindow.get(windowObject);
  if (known) return known;

  const state = { urgent: windowObject.document?.visibilityState === "hidden" };
  const markPageHidden = () => { state.urgent = true; };
  const markPageVisible = () => { state.urgent = false; };
  const handleVisibility = () => {
    state.urgent = windowObject.document?.visibilityState === "hidden";
  };

  // Capture listeners run before the feature lifecycle's bubble listeners, so
  // its exactly-once abandonment append can select urgent delivery synchronously.
  windowObject.addEventListener?.("pagehide", markPageHidden, true);
  windowObject.addEventListener?.("pageshow", markPageVisible, true);
  windowObject.document?.addEventListener?.("visibilitychange", handleVisibility, true);
  pageExitDeliveryByWindow.set(windowObject, state);
  return state;
};

export const readStoredUser = (storage) => {
  if (!storage || typeof storage.getItem !== "function") return {};
  try {
    const value = JSON.parse(storage.getItem("userData") || "{}");
    return value && typeof value === "object" ? value : {};
  } catch (_) {
    return {};
  }
};

export const userIdFrom = (userData) => {
  const candidate = userData?.userId || userData?.uid || userData?.id || userData?.email;
  return typeof candidate === "string" && candidate.trim()
    ? candidate.trim()
    : "anonymous";
};

export const createHistoryRouter = (
  history,
  documentLocation = null,
  documentHistory = null
) => ({
  commit({ href, mode, state }) {
    if (!history || typeof history.push !== "function" || typeof history.replace !== "function") {
      throw new TypeError("A React Router history adapter is required");
    }
    // Crossing the V1/V2 ownership boundary must be a document navigation from
    // the first observable step. Updating React Router before reload briefly
    // mounts the destination inside the previous shell; heavy legacy views can
    // then starve the main thread before reload gets a chance to tear it down.
    const hasOpaqueState = state !== undefined;
    const stateMethod = mode === "document-replace" ? "replaceState" : "pushState";
    if (
      hasOpaqueState &&
      ["document-replace", "document-push"].includes(mode) &&
      typeof documentHistory?.[stateMethod] === "function" &&
      typeof documentLocation?.reload === "function"
    ) {
      documentHistory[stateMethod]({
        key: Math.random().toString(36).slice(2, 8),
        state,
      }, "", href);
      // Native History state does not notify React Router. Reloading after it
      // therefore preserves opaque workflow context without ever mounting the
      // destination under the source interface shell.
      documentLocation.reload();
      return;
    }
    if (mode === "document-replace") {
      if (typeof documentLocation?.replace === "function") {
        documentLocation.replace(href);
      } else {
        history.replace(href);
      }
      return;
    }
    if (mode === "document-push") {
      if (typeof documentLocation?.assign === "function") {
        documentLocation.assign(href);
      } else {
        history.push(href);
      }
      return;
    }

    if (mode === "replace") {
      // Un remplacement interne peut porter un état de navigation (trace du
      // repli V2 → V1) que React Router transmet à la destination.
      if (hasOpaqueState) history.replace(href, state);
      else history.replace(href);
      return;
    }
    history.push(href);
  },
});

export const isBrowserPilotTelemetryEnabled = (windowObject, environment = {}) => {
  const runtimeSwitch = windowObject?.__AKONGO_WELFARE_V2_TELEMETRY_ENABLED__;
  if (runtimeSwitch === false) return false;
  return runtimeSwitch === true ||
    environment?.REACT_APP_WELFARE_V2_TELEMETRY_ENABLED === "true";
};

/**
 * Browser adapter for the runtime publisher already exposed at the application
 * seam. It is deliberately default-off: a deployment must enable telemetry
 * explicitly and inject a batch publisher. No endpoint, credential or vendor
 * is invented here. Missing configuration becomes a disabled adapter and never
 * changes navigation behaviour.
 */
export const createBrowserPilotTelemetry = (
  windowObject,
  environment = process.env
) => {
  if (!isBrowserPilotTelemetryEnabled(windowObject, environment)) {
    return createDisabledPilotTelemetry();
  }

  const publishBatch = windowObject?.__AKONGO_WELFARE_V2_TELEMETRY__;
  if (typeof publishBatch !== "function") {
    return createDisabledPilotTelemetry("transport-unavailable");
  }
  const pageExitDelivery = readPageExitDelivery(windowObject);

  return createQueuedPublishingPilotTelemetry({
    enabled: true,
    publishBatch,
    publishUrgent: (events) => publishBatch(events, {
      delivery: "page-exit",
      keepalive: true,
    }),
    shouldPublishUrgently: () => pageExitDelivery.urgent,
  });
};

export const isLocalAcceptanceRuntime = ({ environment, windowObject } = {}) => {
  if (environment?.NODE_ENV === "development") return true;
  const hostname = String(windowObject?.location?.hostname || "").toLowerCase();
  return hostname === "localhost" || hostname.endsWith(".localhost") ||
    hostname === "127.0.0.1" || hostname === "::1" || hostname === "[::1]";
};

export const shouldReadProductionRollout = (context = {}) => (
  context.environment?.NODE_ENV === "production" &&
  !isLocalAcceptanceRuntime(context)
);

/**
 * Rollout statique du staff AKONGO en production : toutes les destinations V2
 * enregistrées (`candidate` ou `parity-approved`) sont ouvertes, sans cohorte
 * ni liste d'activation. Il remplace la policy Firebase Remote Config
 * (fail-closed, relue toutes les 60 s et à chaque montage de route) dont
 * l'attente réseau retardait le premier rendu de chaque page.
 */
export const AKONGO_STAFF_ROLLOUT = Object.freeze({
  globalV2Enabled: true,
  cohortEligible: true,
  previewCandidates: true,
  enabledDestinations: Object.freeze([]),
  disabledDestinations: Object.freeze([]),
  configurationRevision: "akongo-staff-static",
  policyStatus: "ready",
});

export const buildRollout = ({ userData, environment, windowObject }) => {
  const runtimeSwitch = windowObject?.__AKONGO_WELFARE_V2_ENABLED__;
  const buildSwitch = environment?.REACT_APP_WELFARE_V2_ENABLED;
  const developmentPreview = isLocalAcceptanceRuntime({ environment, windowObject });
  const globalV2Enabled = runtimeSwitch !== false && buildSwitch !== "false";
  const explicitDestinations = Array.isArray(userData?.welfareV2Destinations)
    ? userData.welfareV2Destinations.filter((id) => typeof id === "string")
    : [];

  return Object.freeze({
    globalV2Enabled,
    cohortEligible:
      userData?.welfareV2Pilot === true || developmentPreview,
    previewCandidates: developmentPreview,
    enabledDestinations: Object.freeze(explicitDestinations),
    // Local development is the coexistence and acceptance environment: every
    // registered V2 candidate must remain reachable so it can be compared with
    // its V1 peer. Production keeps the independent rollout blocklist until a
    // destination is explicitly promoted.
    disabledDestinations: developmentPreview
      ? Object.freeze([])
      : LEGACY_ENGINE_PREVIEW_BLOCKLIST,
  });
};

export const locationFromHref = (href) => {
  const [pathname, rawSearch = ""] = String(href || "/").split("?");
  return {
    pathname: pathname || "/",
    search: rawSearch ? `?${rawSearch}` : "",
  };
};
